// Fase 7 Bloco G · Cookie consent helper (client-side).
//
// Persiste a escolha do usuário num cookie `_trakd_cookie_consent` com TTL de
// 1 ano. Categorias: essential (sempre true) · functional · analytics · marketing.
// Tracker JS (Trakd CDN / Meta Pixel auxiliar / etc) verifica `consent.marketing`
// antes de inicializar.
//
// Esse módulo é client-only — não importar em RSC.

export interface CookieConsent {
  essential: true; // sempre true (legítimo interesse)
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  /** ISO timestamp da escolha · usado pra invalidar consent antigo se mudarmos categorias */
  decided_at: string;
}

export const CONSENT_COOKIE_NAME = '_trakd_cookie_consent';
const TTL_SECONDS = 365 * 24 * 60 * 60; // 1 ano

const DEFAULT_REJECTED: Omit<CookieConsent, 'decided_at'> = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
};

const DEFAULT_ACCEPTED: Omit<CookieConsent, 'decided_at'> = {
  essential: true,
  functional: true,
  analytics: true,
  marketing: true,
};

/**
 * Lê o cookie atual. Retorna null se ainda não houve escolha (banner deve aparecer).
 */
export function getConsent(): CookieConsent | null {
  if (typeof document === 'undefined') return null;
  const raw = readCookie(CONSENT_COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as Partial<CookieConsent>;
    if (typeof parsed !== 'object' || !parsed) return null;
    // essential é sempre true (não confiável vir do cookie · força)
    return {
      essential: true,
      functional: Boolean(parsed.functional),
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      decided_at:
        typeof parsed.decided_at === 'string' ? parsed.decided_at : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/**
 * Persiste a escolha. Sempre força essential=true.
 */
export function setConsent(
  input: Partial<Omit<CookieConsent, 'essential' | 'decided_at'>>,
): CookieConsent {
  const value: CookieConsent = {
    essential: true,
    functional: Boolean(input.functional),
    analytics: Boolean(input.analytics),
    marketing: Boolean(input.marketing),
    decided_at: new Date().toISOString(),
  };
  if (typeof document !== 'undefined') {
    writeCookie(CONSENT_COOKIE_NAME, encodeURIComponent(JSON.stringify(value)), TTL_SECONDS);
  }
  // Dispara evento custom para outros listeners no DOM (ex: tracker JS)
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent('trakd:consent-change', { detail: value }));
    } catch {
      /* noop */
    }
  }
  return value;
}

/** Aceita todas as categorias. */
export function acceptAll(): CookieConsent {
  return setConsent(DEFAULT_ACCEPTED);
}

/** Rejeita não-essenciais (só essential fica). */
export function rejectNonEssential(): CookieConsent {
  return setConsent(DEFAULT_REJECTED);
}

// --- helpers ----------------------------------------------------------------

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const [k, ...v] = part.trim().split('=');
    if (k === name) return v.join('=');
  }
  return null;
}

function writeCookie(name: string, value: string, maxAgeSec: number): void {
  const sameSite = 'Lax';
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${value}; Max-Age=${maxAgeSec}; Path=/; SameSite=${sameSite}${secure}`;
}
